import * as React from 'react'
import { getId } from '@basehub/utils/id/index'

import {
  type ListBlock,
  isComponentBlock,
  Block,
  isInstanceBlock,
  MediaBlockValueType,
  MediaBlock,
} from '@basehub/data/replicache/index'

import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { uploadFileToRepo } from '~/lib/file-utils'
import { getFileHashSHA256 } from '@basehub/data/blob/file-utils'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { useSelectedView } from '../use-selected-view'
import { invariant } from '@epic-web/invariant'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import {
  VALID_AUDIO_MIME_TYPES,
  VALID_IMAGE_MIME_TYPES,
  VALID_VIDEO_MIME_TYPES,
} from '@basehub/data/replicache/schema/block/valid-mime-types'
import { Position } from '@basehub/data/replicache/mutators/repo-space/block/helpers/fractional-indexing'
import { toast } from 'sonner'
import { getBlockFromColumnId } from '@basehub/data/replicache/schema/block/list/utils'
import { isError } from '@basehub/utils/error'

const FILES_UPLOAD_CHUNK_SIZE = 50

export const useTableDropAssets = ({
  listBlock,
  path,
}: {
  listBlock: ListBlock
  path: string
}) => {
  const { data } = useRepoReplicache()
  const repoId = data?.repoId
  const teamId = data?.team.id
  const replicache = data?.replicache
  const treeManager = useWorkingTreeStore((s) => s.treeRef.current)
  const getNode = useWorkingTreeStore((s) => s.treeRef.current?.getNode)
  const getPath = useWorkingTreeStore((s) => s.treeRef.current?.getPath)
  const [selectedView, updateSelectedView] = useSelectedView(listBlock)

  const onDrop = React.useCallback(
    async (
      e: React.DragEvent<HTMLElement>,
      position: Position
    ): Promise<Block[]> => {
      if (!replicache || !path || !treeManager) return []
      if (!teamId || !repoId) {
        throw new Error(
          'Could not find team or repo information, this is a bug'
        )
      }

      if (
        !e.dataTransfer ||
        !e.dataTransfer.files ||
        !e.dataTransfer.files[0]
      ) {
        return []
      }

      e.stopPropagation()
      e.preventDefault()

      if (e.dataTransfer.files.length > 200) {
        toast.error('You can only upload 200 files at a time')
        return []
      }

      // 1. get valid files
      const fileNames: string[] = []
      const validFiles: {
        file: File
        type: MediaBlockValueType
      }[] = []

      Array.from(e.dataTransfer.files).forEach((file) => {
        if (!file.type) return
        let type
        if (
          file.type.includes('video/') ||
          file.type.includes('image/') ||
          file.type.includes('audio/')
        ) {
          type = file.type.split('/')[0]
        } else type = 'file'

        fileNames.push(file.name)
        validFiles.push({
          type: type as MediaBlockValueType,
          file,
        })
      }, [])
      if (!validFiles.length) return []

      // 2. create columns
      if (!getNode || !getPath) return []
      const listTemplate = getNode(listBlock.typeOptions.targetBlockId, {
        required: true,
      }).value
      invariant(
        isComponentBlock(listTemplate),
        `listTemplate should be a component block, found ${listTemplate.type}`
      )
      const listTemplatePath = getPath(listTemplate.id)
      if (!listTemplatePath) return []

      const candidates: Block[] = []

      // First check if we have an existing media block that can handle all types
      const columnCandidates = treeManager
        .getChildrenKeys(idToSourceBlockId(listTemplate))
        .map((childId) => {
          const cNode = getNode(childId, { required: true })
          return cNode.value
        })
        .filter((c) => c.type === 'media') as MediaBlock[]

      let mediaBlock: MediaBlock | undefined

      if (!selectedView) {
        mediaBlock = columnCandidates[0]
      } else {
        mediaBlock = columnCandidates.find((candidate) => {
          // Check if this block can handle all our file types
          return validFiles.every(({ file }) => {
            const assetType = getAssetTypeFromFile(file)
            const cat =
              candidate.constraints.allowedMimeTypes?.[assetType] || []
            return cat === true || cat.includes(file.type)
          })
        })
      }

      if (mediaBlock) {
        candidates.push(mediaBlock)
      } else {
        // Create one media block for all files
        const id = getId()
        await replicache.mutate
          .createColumnAndAddToColumnMetadata({
            data: {
              id,
              title: 'Media',
              type: 'media',
              value: { status: 'idle' },
              constraints: {
                isRequired: false,
                allowedMimeTypes: {
                  image: VALID_IMAGE_MIME_TYPES,
                  video: VALID_VIDEO_MIME_TYPES,
                  audio: VALID_AUDIO_MIME_TYPES,
                  file: ['*/*'],
                },
                maxSize: null,
              },
            },
            listBlockId: listBlock.id,
            viewId: selectedView?.id,
            targetBlockId_withinTree: listTemplate.id,
            isoNow: new Date().toISOString(),
            path: listTemplatePath,
            idSeed: getId(),
          })
          .then(({ newColumn, view }) => {
            if (view?.id && view.id !== selectedView?.id) {
              updateSelectedView(view.id)
            }
            candidates.push(newColumn)
            mediaBlock = newColumn as MediaBlock
            return newColumn
          })
      }

      // 3. create rows
      const newBlocks = await replicache.mutate.createListChildrenFromFileNames(
        {
          fileNames,
          listBlockId: listBlock.id,
          path,
          isoNow: new Date().toISOString(),
          position,
          idSeed: getId(),
        }
      )

      const fileChunks = Array.from(
        { length: Math.ceil(validFiles.length / FILES_UPLOAD_CHUNK_SIZE) },
        (_, i) =>
          validFiles.slice(
            i * FILES_UPLOAD_CHUNK_SIZE,
            i * FILES_UPLOAD_CHUNK_SIZE + FILES_UPLOAD_CHUNK_SIZE
          )
      )

      await replicache.mutate.setListChildrenAsUploadingFiles({
        listBlockId: listBlock.id,
        childIdsCellsMap: validFiles
          .map((item, index) => {
            const newBlock = newBlocks[index]
            if (!newBlock) return
            const instance = getNode(newBlock.id, { required: true }).value

            invariant(
              isInstanceBlock(instance),
              'instance should be an instance block'
            )

            if (!mediaBlock) return

            const { block } = getBlockFromColumnId(
              idToSourceBlockId(mediaBlock),
              { row: instance, treeManager }
            )
            if (!block) return

            return {
              id: newBlock.id,
              cellId: block.id,
              targetId: newBlock.instanceChildTarget,
            }
          })
          .filter(Boolean),
        path,
        isoNow: new Date().toISOString(),
      })

      for (const chunk of fileChunks) {
        await Promise.all(
          chunk.map(async (item) => {
            if (!treeManager) return
            const newBlock = newBlocks[chunk.indexOf(item)]
            if (!newBlock) return
            const instance = getNode(newBlock.id, { required: true }).value

            invariant(
              isInstanceBlock(instance),
              'instance should be an instance block'
            )

            if (!mediaBlock) return

            const { block } = getBlockFromColumnId(
              idToSourceBlockId(mediaBlock),
              { row: instance, treeManager }
            )
            if (!block) return

            const blockPath = getPath(block.id)
            if (!blockPath) return

            try {
              const { uploadURL } = await uploadFileToRepo(item.file, {
                teamId,
                repoId,
                fileHash: await getFileHashSHA256(item.file),
              })

              if (getAssetTypeFromFile(item.file) === 'image') {
                const image = new Image()
                const objectUrl = URL.createObjectURL(item.file)
                image.onload = async () => {
                  await replicache.mutate.updateBlock({
                    id: block.id,
                    instanceChildTarget: block.instanceChildTarget,
                    data: {
                      type: 'media',
                      value: {
                        type: 'image',
                        status: 'uploaded',
                        mimeType: item.file.type,
                        fileName: item.file.name,
                        fileSize: item.file.size,
                        url: uploadURL,
                        width: image.width,
                        height: image.height,
                        cloudflareImage: {
                          uploadState: 'will-not-upload',
                          url: null,
                          reason: 'deprecated',
                        },
                        altText: null,
                        lastModified: item.file.lastModified,
                      },
                    },
                    path: blockPath,
                    strategy: 'merge',
                    isoNow: new Date().toISOString(),
                  })
                  URL.revokeObjectURL(objectUrl)
                }
                image.src = objectUrl
              } else if (getAssetTypeFromFile(item.file) === 'video') {
                const videoElement = document.createElement('video')
                videoElement.addEventListener('loadedmetadata', async () => {
                  await replicache.mutate.updateBlock({
                    id: block.id,
                    instanceChildTarget: block.instanceChildTarget,
                    data: {
                      type: 'media',
                      value: {
                        type: 'video',
                        status: 'uploaded',
                        mimeType: item.file.type,
                        fileName: item.file.name,
                        fileSize: item.file.size,
                        width: videoElement.videoWidth,
                        height: videoElement.videoHeight,
                        url: uploadURL,
                        lastModified: item.file.lastModified,
                      },
                    },
                    path: blockPath,
                    strategy: 'merge',
                    isoNow: new Date().toISOString(),
                  })
                  videoElement.remove()
                })
                videoElement.addEventListener('error', () => {
                  videoElement.remove()
                  URL.revokeObjectURL(src)
                })
                const src = URL.createObjectURL(item.file)
                videoElement.src = src
              } else {
                const type = getAssetTypeFromFile(item.file)
                await replicache.mutate.updateBlock({
                  id: block.id,
                  instanceChildTarget: block.instanceChildTarget,
                  data: {
                    type: 'media',
                    value: {
                      type: type === 'audio' ? 'audio' : 'file',
                      status: 'uploaded',
                      mimeType: item.file.type,
                      fileName: item.file.name,
                      fileSize: item.file.size,
                      url: uploadURL,
                      lastModified: item.file.lastModified,
                    },
                  },
                  path: blockPath,
                  strategy: 'merge',
                  isoNow: new Date().toISOString(),
                })
              }
            } catch (error) {
              toast.error(
                `Failed to upload ${item.file.name} (${
                  item.file.type
                }) to ${path}${isError(error) ? `: ${error.message}` : ''}`
              )
            }
          })
        )
      }

      return newBlocks
    },
    [
      replicache,
      path,
      repoId,
      teamId,
      getNode,
      getPath,
      listBlock.typeOptions.targetBlockId,
      listBlock.id,
      selectedView,
      updateSelectedView,
      treeManager,
    ]
  )

  return onDrop
}

const getAssetTypeFromFile = (
  file: File
): 'image' | 'video' | 'audio' | 'file' => {
  if (VALID_IMAGE_MIME_TYPES.includes(file.type)) {
    return 'image'
  }

  if (VALID_VIDEO_MIME_TYPES.includes(file.type)) {
    return 'video'
  }

  if (VALID_AUDIO_MIME_TYPES.includes(file.type)) {
    return 'audio'
  }

  return 'file'
}
